import { createNodeRuleSet, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, TextAlignment, } from "@buildwithflux/models";
import { metersToMm } from "@buildwithflux/shared";
// eslint-disable-next-line boundaries/element-types
import { PcbLayoutRuleCompiler } from "../../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { TextPlaceholder } from "../../SharedDataModels";
import { numberToLetters, parseAlphabet, replaceAutoWithDefault2, replaceAutoWithDefault3 } from "../utils";
import { designatorFontSize, designatorMargin, pinMarkerMargin, pinMarkerRadius, silkWidth } from "./commonConstants";
import { packageSizeMarginPinsX, packageSizeMarginPinsY } from "./gridArrayConstants";
function gridArrayGeneratePinNumber(x, y, i, alphabetX, // numeric if undefined
alphabetY, // numeric if undefined
heuristicUseAlphabet) {
    if (!heuristicUseAlphabet) {
        return `${i + 1}`;
    }
    let str = "";
    if (alphabetY && alphabetY.length > 0) {
        str += numberToLetters(y, alphabetY);
    }
    else {
        str += (y + 1).toString();
    }
    if (alphabetX && alphabetX.length > 0) {
        str += numberToLetters(x, alphabetX);
    }
    else {
        str += (x + 1).toString();
    }
    return str;
}
function gridInferPinLayout(bakedRules, existingPadNodes) {
    const alphabetX = parseAlphabet(bakedRules.gridArrayColumnAlphabet);
    const alphabetY = parseAlphabet(bakedRules.gridArrayRowAlphabet);
    const alphabetPossibleValues = new Set([...(alphabetX ?? []), ...(alphabetY ?? [])]);
    // Both A1-Z9 and 0-99 numeration types are supported. We switch between the two using this heuristic.
    const heuristicUseAlphabet = existingPadNodes.some((pad) => pad.terminalId && `${pad.terminalId}`.split("").some((c) => alphabetPossibleValues.has(c)));
    // To quickly find existing pads by pin number
    const existingPinNumberPadMap = new Map();
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    existingPadNodes.forEach((pad) => {
        if (!pad.terminalId)
            return;
        existingPinNumberPadMap.set(`${pad.terminalId}`, pad);
    });
    // We support "provisional" ordering using the pad designator
    const withoutPinNumberPadNodes = existingPadNodes.filter((pad) => `${pad.terminalId}` === "" || pad.terminalId === undefined);
    withoutPinNumberPadNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const result = { grid: [], otherPads: [] };
    // We populate all the grid spaces
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const gridY = bakedRules.gridArrayCount?.y ?? 0;
    for (let gy = 0; gy < gridY; gy++) {
        for (let gx = 0; gx < gridX; gx++) {
            const realGY = gridY - 1 - gy;
            const pinNumber = gridArrayGeneratePinNumber(gx, gy, gx + gy * gridX, alphabetX, alphabetY, heuristicUseAlphabet);
            const row = result.grid[realGY] ?? [];
            result.grid[realGY] = row;
            const matchingPad = existingPinNumberPadMap.get(pinNumber);
            if (matchingPad) {
                row[gx] = matchingPad;
            }
            else {
                // fallback: get from the unnumbered ones
                const unnumberedPin = withoutPinNumberPadNodes.shift();
                if (unnumberedPin) {
                    row[gx] = unnumberedPin;
                }
            }
        }
    }
    return result;
}
export function gridArrayComputeInferredRules(bakedRules, config, existingPadNodes) {
    const pinSize = replaceAutoWithDefault2(bakedRules.pinSize, { x: config.pinSize, y: config.pinSize });
    const pinPitch = replaceAutoWithDefault2(bakedRules.pinPitch, { x: config.pinPitch, y: config.pinPitch });
    // Try to infer the grid array count if not set
    const padCountX = Math.ceil(Math.sqrt(existingPadNodes.length));
    const errorX = Math.sqrt(existingPadNodes.length) - padCountX;
    const padCountY = Math.ceil(Math.sqrt(existingPadNodes.length) + errorX);
    const gridArrayCount = replaceAutoWithDefault2(bakedRules.gridArrayCount, {
        x: config.gridArrayCountX ?? padCountX,
        y: config.gridArrayCountY ?? padCountY,
    });
    const packageSize = replaceAutoWithDefault3(bakedRules.packageSize, {
        x: (gridArrayCount.x + packageSizeMarginPinsX) * pinPitch.x,
        y: (gridArrayCount.y + packageSizeMarginPinsY) * pinPitch.y,
        z: config.packageHeight,
    });
    const newBakedRules = {
        ...bakedRules,
        packageSize,
        pinSize,
        pinPitch,
        gridArrayCount,
    };
    return { ...newBakedRules, pinLayout: gridInferPinLayout(newBakedRules, existingPadNodes) };
}
export function gridArrayGeneratePadRules(footprintNodeUid, bakedRules) {
    const result = {};
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const gridY = bakedRules.gridArrayCount?.y ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const pinPitchY = bakedRules.pinPitch?.y ?? 0;
    const totalWidth = (gridX - 1) * pinPitchX;
    const totalHeight = (gridY - 1) * pinPitchY;
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return result;
    for (let gy = 0; gy < gridY; gy++) {
        for (let gx = 0; gx < gridX; gx++) {
            const matchingPad = pinLayout.grid[gy]?.[gx];
            if (matchingPad) {
                const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPad.type, matchingPad.uid);
                const ruleSet = {
                    // NOTE: the ruleset ID and the node ID is assumed to be the same elsewhere.
                    // See PcbSelectorMatcher.
                    uid: matchingPad.uid,
                    selector: selector,
                    rules: {},
                    generatedFromAncestorNodeUid: footprintNodeUid,
                };
                result[ruleSet.uid] = ruleSet;
                const posX = gx * pinPitchX - totalWidth / 2;
                const posY = gy * pinPitchY - totalHeight / 2;
                const rules = {
                    position: `${metersToMm(posX)}mm ${metersToMm(posY)}mm`,
                    size: `${metersToMm(pinSizeX)}mm ${metersToMm(pinSizeY)}mm`,
                };
                ruleSet.rules = createNodeRuleSet(rules);
            }
        }
    }
    return result;
}
export function gridArrayGenerateSilkNodes(footprintNodeUid, bakedRules) {
    if (!bakedRules.packageSize)
        return [];
    const gridY = bakedRules.gridArrayCount?.y ?? 0;
    const pinPitchY = bakedRules.pinPitch?.y ?? 0;
    const totalHeight = (gridY - 1) * pinPitchY;
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    const packageWidth = bakedRules.packageSize.x;
    const packageHeight = bakedRules.packageSize.y;
    // outer shell
    silkPaths.push(`<path d="
                M ${-packageWidth / 2} ${-packageHeight / 2}
                L ${packageWidth / 2} ${-packageHeight / 2}
                L ${packageWidth / 2} ${packageHeight / 2}
                L ${-packageWidth / 2} ${packageHeight / 2}
                Z" stroke-width="${silkWidth}"
                stroke-linecap="round" fill="none" />`);
    // pin 1
    const pin1Y = -totalHeight / 2;
    silkPaths.push(`<circle
                cx="${-packageWidth / 2 - pinMarkerMargin}"
                cy="${pin1Y}"
                r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    // Designator text
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(0)}mm ${metersToMm(packageHeight / 2 + designatorMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
//# sourceMappingURL=gridArray.js.map